import rest from "@/rest-client";

export default function createCrudVuexModule({
  namespaced = true,
  options = { localizedFields: ["name", "description"] },
  path,
  moduleName,
  state,
  getters,
  mutations,
  actions,
  persistent,
  preSave,
}) {
  return {
    moduleName,
    namespaced: namespaced,
    persistent,

    state: {
      copingEntity: undefined,
      editedEntity: undefined,
      selectedEntity: undefined,
      pagination: undefined,
      paginationForSearch: undefined,
      isSearchLoading: false,
      isLoading: false,
      isUpdating: false,
      isCoping: false,

      request: {
        options: { page: 1, limit: 15 },
        query: { name: { value: "" } },
      },
      deleteOperationResult: {
        success: [],
        failed: [],
      },
      addOperationResult: {
        success: [],
        failed: [],
      },
      ...state,
    },
    getters: {
      copingEntity: (state) => state.copingEntity ?? {},
      editedEntity: (state) => state.editedEntity ?? {},
      selectedEntity: (state) => state.selectedEntity,
      pagination: (state) => state.pagination,
      isLoading: (state) => state.isLoading,
      isUpdating: (state) => state.isUpdating,
      isEditMode: (state) => !!state.editedEntity?._id,
      deleteOperationResult: (state) => state.deleteOperationResult,
      addOperationResult: (state) => state.addOperationResult,
      paginationForSearch: (state) => state.paginationForSearch,
      isSearchLoading: (state) => state.isSearchLoading,
      isCoping: (state) => !!state.copingEntity && Object.keys(state.copingEntity).length > 0 && state.copingEntity.coping,

      ...getters,
    },
    mutations: {
      editedEntity: (state, value) => (state.editedEntity = value),
      selectedEntity: (state, value) => (state.selectedEntity = value),
      pagination: (state, value) => (state.pagination = value),
      graph: (state, value) => (state.graph = value),
      isLoading: (state, value) => (state.isLoading = value),
      isUpdating: (state, value) => (state.isUpdating = value),
      request: (state, value) => (state.request = value),
      paginationForSearch: (state, value) => (state.paginationForSearch = value),
      isSearchLoading: (state, value) => (state.isSearchLoading = value),
      deleteOperationResult: (state, value) => (state.deleteOperationResult = value),
      addOperationResult: (state, value) => (state.addOperationResult = value),
      copingEntity: (state, value) => {
        if (value) {
          delete value._id;
          state.copingEntity = {
            ...value,
            libraryItem: null /* TN-234 Kütüphaneye farklı kaydet ile oluşturulan isim kaydedilmesi için  */,
            libraryReference: value.libraryItem /*libraryItem'ın kullanıldığı yerler var o sebeple farklı bir isimle gönderdim  */,
            coping: true,
          };
        } else {
          state.copingEntity = true;
        }
      },
      ...mutations,
    },
    actions: {
      async save({ dispatch }, payload) {
        if (preSave) {
          payload = await preSave(payload);
        }

        if (payload._id) {
          return dispatch("update", payload);
        } else {
          return dispatch("create", payload);
        }
      },

      async create({ getters, commit, dispatch }, payload) {
        try {
          commit("isUpdating", true);
          payload = await dispatch("preparePayload", {
            editedEntity: getters.editedEntity,
            form: payload,
          });

          const response = await rest.post(`${path}`, { item: payload });
          commit("editedEntity", undefined);
          return response;
        } finally {
          commit("isUpdating", false);
        }
      },

      async update({ getters, commit, dispatch }, payload) {
        try {
          commit("isUpdating", true);
          payload = await dispatch("preparePayload", {
            editedEntity: getters.editedEntity,
            form: payload,
          });

          const response = await rest.put(`${path}`, { item: payload });

          commit("editedEntity", undefined);
          return response;
        } finally {
          commit("isUpdating", false);
        }
      },

      async paginate({ commit }, pagination, clearStateBeforeRest = false) {
        commit("isLoading", true);
        try {
          if (clearStateBeforeRest) {
            commit("pagination", undefined);
          }
          const response = await rest.post(`${path}/paginate`, { pagination });
          commit("pagination", response.data);
          return response;
        } finally {
          commit("isLoading", false);
        }
      },

      async paginateWith({ commit }, payload = {}, clearStateBeforeRest = false) {
        commit("isLoading", true);
        try {
          if (clearStateBeforeRest) {
            commit("pagination", undefined);
          }
          const response = await rest.post(`${path}/paginate`, { ...payload });
          commit("pagination", response.data);
          return response;
        } finally {
          commit("isLoading", false);
        }
      },
      async paginateForSearch({ commit }, pagination, clearStateBeforeRest = false) {
        commit("isSearchLoading", true);
        try {
          if (clearStateBeforeRest) {
            commit("paginationForSearch", undefined);
          }
          const response = await rest.post(`${path}/paginate`, { pagination });
          commit("paginationForSearch", response.data);
          return response;
        } finally {
          commit("isSearchLoading", false);
        }
      },
      async findById({ commit }, id) {
        commit("isLoading", true);
        try {
          commit("selectedEntity", undefined);
          const response = await rest.get(`${path}/${id}`);
          commit("selectedEntity", response.data);
          return response;
        } finally {
          commit("isLoading", false);
        }
      },

      /**
       * Bu method flat objeyi localize eder.
       * @editedEntity : dbden gelen güncellenecek kayıt
       * @form : arayüz formundan gelen veriler
       *
       *
       * form->{
       *    name:"Arge"
       * }
       *
       * output->{
       *    name:{
       *      tr:"Arge"
       *    }
       * }
       *
       */
      preparePayload({ commit }, { editedEntity, form }) {
        const lang = this.getters["user/account"].language;
        const payload = {
          ...editedEntity,
          ...form,
        };
        // localifie

        options.localizedFields.forEach((field) => {
          if (typeof form[field] != "object") {
            if (payload[field] == null || payload[field] == undefined) {
              return;
            }
            payload[field] = {
              ...editedEntity[field],
              [lang]: form[field]?.trim(),
            };
          }
        });

        return payload;
      },

      async delete({ commit }, id) {
        return rest.delete(`${path}/${id}`);
      },

      async multipleDelete({ state, commit }, array) {
        const success = [];
        const failed = [];

        for (const item of array) {
          try {
            await rest.delete(`${path}/${item._id}`);
            success.push(item.name);
          } catch (error) {
            failed.push({ name: item.name, error: error.data.message });
          }
        }

        commit("deleteOperationResult", {
          success: success,
          failed: failed,
        });
      },
      async multipleAddFromLibrary({ state, commit, dispatch }, array) {
        const success = [];
        const failed = [];

        for (const item of array) {
          try {
            await dispatch("addFromLibrary", item);

            success.push({ name: item?.libraryItem?.name, _id: item?.libraryItem?._id });
          } catch (error) {
            failed.push({ name: item.libraryItem?.name, _id: item?.libraryItem?._id, error: error.data.message });
          }
        }

        commit("addOperationResult", {
          success: success,
          failed: failed,
        });
      },
      async resetForm({ commit }) {
        commit("selectedEntity", null);
        commit("editedEntity", null);
        commit("copingEntity", null);
      },
      ...actions,
    },
  };
}
